import { accounting } from '@/localize.js'
import { getConfig } from '@/api'
import { CONNECTION_PROBLEMS } from '@/lib/consts.ts'

import {
  createHCaptcha,
} from '@booksy-com/vue-tools'

const CONFIG_RELOAD_MAX_RETRIES = 3
const DEFAULT_MASS_PRECISION = 3
const DEFAULT_CURRENCY_PRECISION = 2

export default {
  namespaced: true,

  /*!
   *
   * state
   *
   */
  state: {
    config: null,
    connectionProblem: null,
  },

  /*!
   *
   * actions
   *
   */
  actions: {
    load ({ commit, dispatch, rootState }, retry = 1) {
      const { country } = rootState.app

      return getConfig(country).then(({ data }) => {
        commit('setConfig', data)

        try {
          // no frontdesk feature flags created; toggled by a `hcaptcha_business_frontdesk_enabled` kill-switch only
          if (data?.hcaptcha_business_frontdesk_enabled) {
            createHCaptcha({
              key: data?.hcaptcha_site_keys?.web_biz,
              recaptchacompat: 'off',
            })
          }
        } catch (e) {
          console.error(e)
        }

        return data
      }).catch(error => {
        if (retry >= CONFIG_RELOAD_MAX_RETRIES) {
          if (error.response && error.response.status >= 500) {
            commit('setConnectionProblem', CONNECTION_PROBLEMS.NO_SERVER_CONNECTION)
          } else if (error.message === 'Network Error' || !navigator.onLine) {
            commit('setConnectionProblem', CONNECTION_PROBLEMS.NO_NETWORK_CONNECTION)
          }

          throw error
        }
        return dispatch('load', retry + 1)
      })
    },
  },

  /*!
   *
   * mutations
   *
   */
  mutations: {
    setConfig (state, config) {
      state.config = config
    },
    setConnectionProblem (state, connectionProblem) {
      state.connectionProblem = connectionProblem
    },
  },

  /*
   *
   * getters
   *
   */
  getters: {
    roundingPrecision (state) {
      let defaultCurrency = state.config?.default_currency

      return state.config?.currency[defaultCurrency]?.decimal_length || DEFAULT_CURRENCY_PRECISION
    },

    roundingFactor: (state, getters) => {
      return state.config && Math.pow(10, getters.roundingPrecision)
    },

    roundingValue: (state, getters) => {
      return state.config && 1 / getters.roundingFactor
    },

    formatCurrency: (state, getters) => {
      return value => {
        value = String(value).replace(/[^0-9.,-]/g, '')
        let number = (accounting.settings.currency.decimal === ',') ? value.replace(',', '.') : value

        number = Math.round(number * getters.roundingFactor) / getters.roundingFactor
        return (isNaN(number)) ? '0' : Number(number).toFixed(getters.roundingPrecision)
      }
    },

    massRoundingPrecision (state, getters) {
      return getters.massUnit?.decimal_length || DEFAULT_MASS_PRECISION
    },

    massUnit (state) {
      let defaultMassUnit = state.config?.default_mass_unit

      return state.config?.mass_unit[defaultMassUnit]
    },

    webToolsUrl (state) {
      return state.config?.web_tools_url?.replace(/\/+$/, '')
    },

    shouldUseMarketPayOnB2bReferral (state) {
      return state.config?.market_pay_b2b_referral
    },

    shouldUseTapToPay (state) {
      return state.config?.pos__tap_to_pay
    },

    isVouchersOnlineSaleEnabled (state) {
      return state.config?.vouchers_online_sale
    },

    shouldUseBooksyGiftCards (state) {
      return state.config?.booksy_gift_cards_enabled
    },

    shouldUseBlik (state) {
      return state.config?.pos__blik
    },

    isBooksyGiftCardsEnabled: state => state.config?.booksy_gift_cards_enabled,

    shouldUseBooksyPay: state => state.config?.pos__booksy_pay,

    isBooksyWalletEnabled: state => state.config?.booksy_wallet_enabled,
  },

}
